import React, { useState, useEffect } from "react";
import { Global } from "@emotion/react";
import {
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Typography,
  Box,
  OutlinedInput,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { countries } from "../../utils/country";
import useNotification from "../../hooks/useNotification";
import NotificationSnackbar from "../../components/NotificationSnackbar";

interface SettingsProps {
  auth?: {
    id?: number; // Deprecated - use consumerId instead
    consumerId?: number; // Primary consumer identifier
    isAuthenticated?: boolean; // Authentication status
    name?: string;
    email?: string;
    telephone?: string;
    address?: string;
    address2?: string;
    twoFactorMethod?: string;
    country?: string;
    state?: string;
    city?: string;
    postcode?: string;
    ssoProvider?: string | null; // Social login provider (google, apple, facebook)
  };
  onStateChange?: (state: any) => void;
  onLogout?: () => void;
  hideTabs?: boolean;
  getConsumerToken?: () => string | null;
  makeConsumerRequest?: (url: string, options?: RequestInit) => Promise<Response>;
}

const twoFactorOptions = [
  { value: "email", label: "Email" },
  { value: "app", label: "Authenticator App" },
];

const Settings: React.FC<SettingsProps> = ({
  auth = {},
  onStateChange,
  onLogout,
  getConsumerToken,
  makeConsumerRequest,
}) => {
  // Responsive breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600-900px

  // --- Phone code extraction logic ---
  let telephoneAuth = auth.telephone || "";
  let countryCode = auth.country || "GB";
  let phoneCodeAuth =
    countries.find((c) => c.code === countryCode)?.phoneCode || "+44";
  // Remove phone code from telephone if present
  if (telephoneAuth.startsWith(phoneCodeAuth)) {
    telephoneAuth = telephoneAuth.replace(phoneCodeAuth, "").trim();
  }
  const [fields, setFields] = useState({
    name: auth.name || "",
    email: auth.email || "",
    telephone: telephoneAuth,
    address: auth.address || "",
    address2: auth.address2 || "",
    twoFactorMethod: auth.twoFactorMethod || "none",
    country: auth.country || "GB",
    state: auth.state || "",
    city: auth.city || "",
    postcode: auth.postcode || "",
    existingPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const { notification, showNotification, hideNotification } =
    useNotification();

  const [phoneCode, setPhoneCode] = useState(
    countries.find((c) => c.code === fields.country)?.phoneCode || "+44"
  );

  useEffect(() => {
    const selected = countries.find((c) => c.code === fields.country);
    setPhoneCode(selected ? selected.phoneCode : "+44");
  }, [fields.country]);

  const handleSubmitChanges = async () => {
    if (!auth?.consumerId || !auth?.isAuthenticated) {
      showNotification("Consumer ID is missing. Please log in again.", "error");
      return;
    }
    try {
      const token = getConsumerToken?.() || localStorage.getItem("consumerToken");
      const response = makeConsumerRequest ?
        await makeConsumerRequest(`/api/consumer/updateConsumer/${auth.consumerId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            Id: auth.consumerId,
            ConsumerId: auth.consumerId,
            Name: fields.name,
            Email: fields.email,
            Phone: phoneCode + fields.telephone,
            TwoFactorMethod: fields.twoFactorMethod,
            Address: fields.address,
            Address2: fields.address2,
            TownCity: fields.city,
            CountryState: fields.state,
            PostCode: fields.postcode,
            Country: fields.country,
            IsActive: true, // Set as needed
          }),
        }) :
        await fetch(`/api/consumer/updateConsumer/${auth.consumerId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            Id: auth.consumerId,
            ConsumerId: auth.consumerId,
            Name: fields.name,
            Email: fields.email,
            Phone: phoneCode + fields.telephone,
            TwoFactorMethod: fields.twoFactorMethod,
            Address: fields.address,
            Address2: fields.address2,
            TownCity: fields.city,
            CountryState: fields.state,
            PostCode: fields.postcode,
            Country: fields.country,
            IsActive: true, // Set as needed
          }),
        });
      if (!response.ok) {
        const errorData = await response.json();
        showNotification(
          errorData.message || "Update failed. Please try again.",
          "error"
        );
        return;
      }
      showNotification("Changes updated successfully", "success");
      // Optimistically update local fields state for instant UI update
      // --- Phone code extraction logic ---
      let telephoneExtract = fields.telephone || "";
      let countryCode = fields.country || "GB";
      let phoneCode2 =
        countries.find((c) => c.code === countryCode)?.phoneCode || "+44";
      // Remove phone code from telephone if present
      if (telephoneExtract.startsWith(phoneCode2)) {
        telephoneExtract = telephoneExtract.replace(phoneCode2, "").trim();
      }
      setFields((prev) => ({
        ...prev,
        name: fields.name,
        email: fields.email,
        telephone: telephoneExtract,
        address: fields.address,
        address2: fields.address2,
        twoFactorMethod: fields.twoFactorMethod,
        country: fields.country,
        state: fields.state,
        city: fields.city,
        postcode: fields.postcode,
      }));
    } catch (error) {
      console.error("Error updating consumer:", error);
      showNotification(
        "An unexpected error occurred. Please try again later.",
        "error"
      );
    }
  };
  const [qrCodeUrl, setQrCodeUrl] = useState(null);

  // Fetch QR code if 2FA method is 'app'
  useEffect(() => {
    if (fields.twoFactorMethod === "app") {
      const token = getConsumerToken?.() || localStorage.getItem("consumerToken");
      // Updated endpoint to match backend
      const fetchQR = makeConsumerRequest ?
        makeConsumerRequest("/api/consumer/2fa/qrcode") :
        fetch("/api/consumer/2fa/qrcode", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
      fetchQR
        .then((res) =>
          res.ok ? res.json() : Promise.reject("Failed to fetch QR code")
        )
        .then((data) => setQrCodeUrl(data.qrCodeUrl || data.qrCode || null))
        .catch(() => setQrCodeUrl(null));
    } else {
      setQrCodeUrl(null);
    }
  }, [fields.twoFactorMethod]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }
    >
  ) => {
    const { name, value } = e.target;
    if (!name) return;
    setFields((prev: typeof fields) => ({ ...prev, [name]: value as string }));
  };

  const handleDetailsChange = (field: any, value: any) => {
    setFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    if (!name) return;
    setFields((prev: typeof fields) => ({ ...prev, [name]: value }));
  };

  const handlePasswordUpdate = async () => {
    if (!auth?.consumerId || !auth?.isAuthenticated) {
      showNotification("Consumer ID is missing. Please log in again.", "error");
      return;
    }

    try {
      const token = getConsumerToken?.() || localStorage.getItem("consumerToken");
      const response = makeConsumerRequest ?
        await makeConsumerRequest("/api/consumer/update-password", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            consumerId: auth.consumerId, // Ensure valid consumer ID is sent
            existingPassword: fields.existingPassword,
            newPassword: fields.newPassword,
            confirmPassword: fields.confirmPassword,
          }),
        }) :
        await fetch("/api/consumer/update-password", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            consumerId: auth.consumerId, // Ensure valid consumer ID is sent
            existingPassword: fields.existingPassword,
            newPassword: fields.newPassword,
            confirmPassword: fields.confirmPassword,
          }),
        });

      if (!response.ok) {
        const errorData = await response.json();
        showNotification(
          errorData.message || "Password update failed. Please try again.",
          "error"
        );
        return;
      }

      showNotification("Password updated successfully", "success");
      setFields((prev) => ({
        ...prev,
        existingPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));
    } catch (error) {
      console.error("Error updating password:", error);
      showNotification(
        "An unexpected error occurred. Please try again later.",
        "error"
      );
    }
  };

  return (
    <>
      <Global
        styles={{
          ".MuiOutlinedInput-root": {
            height: "30px !important",
          },
        }}
      />
      <Box 
        className="settings-container"
        sx={{ 
          p: isMobile ? 1 : 3,
        }}
      >
        {/* Responsive CSS Grid Layout */}
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',           // 1 column on mobile
              sm: 'repeat(2, 1fr)', // 2 columns on tablet
              md: 'repeat(3, 1fr)'  // 3 columns on desktop
            },
            gap: isMobile ? 1 : 2,
            mb: 2,
            width: '100%',
            maxWidth: '100%',
          }}
        >
          {/* Column 1: Name, Address, County/State, Postcode */}
          <Box>
            <TextField
              label="Name"
              name="name"
              value={fields.name}
              autoFocus
              onChange={(e) => {
                const value = e.target.value.replace(/[^A-Za-z ]/g, "");
                setFields((prev) => ({ ...prev, name: value }));
              }}
              inputProps={{ maxLength: 50, pattern: "[A-Za-z ]*" }}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                marginBottom: 2,
                "& .MuiInputBase-input": {
                  fontSize: 13,
                  height: 20,
                  padding: "2px 8px",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputLabel-root": { fontSize: 13 },
                "& .MuiOutlinedInput-root": { minHeight: 20, padding: 0 },
              }}
            />
            <TextField
              label="Address"
              name="address"
              value={fields.address}
              onChange={handleChange}
              inputProps={{ maxLength: 100 }}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                marginBottom: 2,
                "& .MuiInputBase-input": {
                  fontSize: 13,
                  height: 20,
                  padding: "2px 8px",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputLabel-root": { fontSize: 13 },
                "& .MuiOutlinedInput-root": { minHeight: 20, padding: 0 },
              }}
            />
            <TextField
              label="County/State"
              name="state"
              value={fields.state}
              onChange={(e) => {
                const value = e.target.value.replace(/[^A-Za-z ]/g, "");
                setFields((prev) => ({ ...prev, state: value }));
              }}
              inputProps={{ maxLength: 50, pattern: "[A-Za-z ]*" }}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                marginBottom: 2,
                "& .MuiInputBase-input": {
                  fontSize: 13,
                  height: 20,
                  padding: "2px 8px",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputLabel-root": { fontSize: 13 },
                "& .MuiOutlinedInput-root": { minHeight: 20, padding: 0 },
              }}
            />
            <TextField
              label="Postcode/Zipcode"
              name="postcode"
              value={fields.postcode}
              onChange={(e) => {
                const value = e.target.value;
                setFields((prev) => ({ ...prev, postcode: value }));
              }}
              inputProps={{ maxLength: 10, pattern: "[0-9 ]*" }}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                marginBottom: 2,
                "& .MuiInputBase-input": {
                  fontSize: 13,
                  height: 20,
                  padding: "2px 8px",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputLabel-root": { fontSize: 13 },
                "& .MuiOutlinedInput-root": { minHeight: 20, padding: 0 },
              }}
            />
          </Box>

          {/* Column 2: Email, Address2, Town/City, Country */}
          <Box>
            <TextField
              label="Email"
              name="email"
              value={fields.email}
              onChange={handleChange}
              inputProps={{ maxLength: 255 }}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                marginBottom: 2,
                "& .MuiInputBase-input": {
                  fontSize: 13,
                  height: 20,
                  padding: "2px 8px",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputLabel-root": { fontSize: 13 },
                "& .MuiOutlinedInput-root": { minHeight: 20, padding: 0 },
              }}
            />
            <TextField
              label="Address 2"
              name="address2"
              value={fields.address2}
              onChange={handleChange}
              inputProps={{ maxLength: 100 }}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                marginBottom: 2,
                "& .MuiInputBase-input": {
                  fontSize: 13,
                  height: 20,
                  padding: "2px 8px",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputLabel-root": { fontSize: 13 },
                "& .MuiOutlinedInput-root": { minHeight: 20, padding: 0 },
              }}
            />
            <TextField
              label="Town/City"
              name="city"
              value={fields.city}
              onChange={handleChange}
              inputProps={{ maxLength: 50 }}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                marginBottom: 2,
                "& .MuiInputBase-input": {
                  fontSize: 13,
                  height: 20,
                  padding: "2px 8px",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputLabel-root": { fontSize: 13 },
                "& .MuiOutlinedInput-root": { minHeight: 20, padding: 0 },
              }}
            />
            <FormControl
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  minHeight: 20,
                },
                "& .MuiInputBase-input": {
                  fontSize: 13,
                  height: 20,
                  padding: "2px 8px",
                },
                "& .MuiInputLabel-root": { fontSize: 13 },
              }}
              size="small"
            >
              <InputLabel htmlFor="country">Country</InputLabel>
              <Select
                name="country"
                value={fields.country}
                onChange={handleSelectChange}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 180,
                      overflowY: "auto",
                    },
                  },
                }}
                input={<OutlinedInput label="Country" placeholder="Country" />}
              >
                {countries.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Column 3: Telephone, 2FA, QR Code */}
          <Box>
            <TextField
              fullWidth
              label="Telephone"
              name="telephone"
              required
              value={fields.telephone}
              onChange={(e) =>
                handleDetailsChange(
                  "telephone",
                  e.target.value.replace(/[^0-9]/g, "")
                )
              }
              sx={{
                marginBottom: 2,
                "& .MuiInputBase-input": {
                  fontSize: 13,
                  height: 20,
                  padding: "2px 8px",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputLabel-root": { fontSize: 13 },
                "& .MuiOutlinedInput-root": { minHeight: 20, padding: 0 },
              }}
              inputProps={{ maxLength: 15 }}
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      fontSize: 13,
                      minWidth: 48,
                      mr: 1,
                      px: 1,
                      borderRight: "2px solid #E9E9E9",
                    }}
                  >
                    {phoneCode}
                  </Box>
                ),
              }}
            />

            {/* 2FA Section - Only show for non-social login users */}
            {!auth.ssoProvider && (
              <FormControl
                fullWidth
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: 20,
                  },
                  "& .MuiInputBase-input": {
                    fontSize: 13,
                    height: 20,
                    padding: "2px 8px",
                  },
                  "& .MuiInputLabel-root": { fontSize: 13 },
                }}
                size="small"
              >
                <InputLabel htmlFor="2-factor-method">2 Factor Method</InputLabel>
                <Select
                  name="twoFactorMethod"
                  value={fields.twoFactorMethod}
                  onChange={handleSelectChange}
                  input={
                    <OutlinedInput
                      label="2 Factor Method"
                      placeholder="2 Factor Method"
                    />
                  }
                >
                  {twoFactorOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {/* Show QR code if Authenticator App is selected */}
            {!auth.ssoProvider && fields.twoFactorMethod === "app" && qrCodeUrl && (
              <Box
                mt={2}
                sx={{
                  width: isMobile ? 100 : 130,
                  height: isMobile ? 100 : 130,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img
                  src={qrCodeUrl}
                  alt="Authenticator QR Code"
                  style={{
                    width: isMobile ? 90 : 120,
                    height: isMobile ? 90 : 120,
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* Submit Changes Button Row */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: { xs: 'center', md: 'flex-end' },
            mb: 2 
          }}
        >
          <Box sx={{ width: { xs: '100%', sm: '50%', md: '33%' } }}>
            <Button
              variant="contained"
              onClick={handleSubmitChanges}
              fullWidth
              sx={{
                textTransform: "none",
                fontSize: 13,
                maxHeight: 30,
                backgroundColor: "#a12a22",
                "&:hover": { backgroundColor: "#b71c1c" },
              }}
            >
              Submit Changes
            </Button>
          </Box>
        </Box>

        {/* Update Password Section - Only show for non-social login users */}
        {!auth.ssoProvider && (
          <>
            <Typography variant="h6" mb={2} sx={{ fontSize: isMobile ? 16 : 20 }}>
              Update Password
            </Typography>
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)'
            },
            gap: isMobile ? 1 : 2,
            mb: 2,
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <Box>
            <TextField
              label="Existing Password"
              name="existingPassword"
              type="password"
              value={fields.existingPassword}
              onChange={handleChange}
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{ maxLength: 64 }}
              sx={{
                marginBottom: 2,
                "& .MuiInputBase-input": {
                  fontSize: 13,
                  height: 20,
                  padding: "2px 8px",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputLabel-root": { fontSize: 13 },
                "& .MuiOutlinedInput-root": { minHeight: 20, padding: 0 },
              }}
            />
          </Box>
          <Box>
            <TextField
              label="New Password"
              name="newPassword"
              type="password"
              value={fields.newPassword}
              onChange={handleChange}
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{ maxLength: 64 }}
              sx={{
                marginBottom: 2,
                "& .MuiInputBase-input": {
                  fontSize: 13,
                  height: 20,
                  padding: "2px 8px",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputLabel-root": { fontSize: 13 },
                "& .MuiOutlinedInput-root": { minHeight: 20, padding: 0 },
              }}
            />
          </Box>
          <Box>
            <TextField
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              value={fields.confirmPassword}
              onChange={handleChange}
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{ maxLength: 64 }}
              sx={{
                marginBottom: 2,
                "& .MuiInputBase-input": {
                  fontSize: 13,
                  height: 20,
                  padding: "2px 8px",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputLabel-root": { fontSize: 13 },
                "& .MuiOutlinedInput-root": { minHeight: 20, padding: 0 },
              }}
            />
          </Box>
          <Box>
            <Button
              variant="contained"
              onClick={handlePasswordUpdate}
              fullWidth
              sx={{
                textTransform: "none",
                fontSize: 13,
                maxHeight: 30,
                backgroundColor: "#a12a22",
                "&:hover": { backgroundColor: "#b71c1c" },
              }}
            >
              Update Password
            </Button>
          </Box>
        </Box>
          </>
        )}

        {/* Sign Out Button */}
        <Box mt={3}>
          <Button
            variant="outlined"
            onClick={onLogout}
            sx={{
              fontWeight: 400,
              color: "#a12a22",
              borderColor: "#a12a22",
              paddingX: 4,
              "&:hover": { borderWidth: 2, fontWeight: 600 },
            }}
          >
            Sign Out
          </Button>
        </Box>
      </Box>
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
    </>
  );
};

export default Settings;
